import classNames from 'classnames'
import { JSX, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'

import { IForm } from 'core-components/classes/models/IForm'
import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { NotificationController } from 'core-components/library/popup'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { useUserStore } from 'src/core/hooks/services/useUserStore'
import { selectAuthError } from 'src/core/store/selectors/selectAuthError'

import { GuestLogin } from './GuestLogin'
import { useProcessLogin } from './login/hooks'

import styles from './Login.module.scss'

interface IContentData {
  onSubmit: (loginResult?: boolean, additionalPayload?: IForm) => void | Promise<void>
  progress: boolean
}

interface ILayoutLoginProps {
  guestLogin?: boolean
  content?: ({ onSubmit }: IContentData) => JSX.Element
  fullScreen?: boolean
}

export const LayoutLogin = ({ content, guestLogin, fullScreen = true }: ILayoutLoginProps) => {
  const user = useSelector(selectUser)
  const isGuest = useUserStore().isGuestUser()
  const authError = useSelector(selectAuthError)
  const { processLogin, isLoginProcessing, initiateLogin } = useProcessLogin()

  useEffect(() => {
    if (authError.message) {
      NotificationController.showError({ message: authError.message })
    }
  }, [authError])

  useEffect(() => {
    document.title = 'Login'
  }, [])

  if (user.email && !isGuest) {
    return <Navigate to={'/'} />
  }

  if (content) {
    return content({ onSubmit: initiateLogin, progress: isLoginProcessing })
  }

  return (
    <div className={classNames(styles.root, fullScreen ? styles.fullScreen : '')}>
      <div className={styles.centering}>
        <div className={styles.paper}>
          <div className={styles.avatar}>
            <svg
              className={styles.logo}
              xmlns="http://www.w3.org/2000/svg"
              fill="#fff"
              viewBox="0 0 32 32"
            >
              <path d="M25.1 11.4h-2.4l-3.3 9.2h2.4l3.3-9.2zM16 31h-.4l3.1-8.4h4.2c.7 0 1.6-.6 1.8-1.3l3.3-9.1c.5-1.5-.3-2.7-1.9-2.7h-5.9L15.3 23l-2.8 7.6C5.9 29 1 23.1 1 16 1 9.1 5.6 3.3 12 1.6L9.1 9.4 4.3 22.6h3.2l4.1-11.1H14L9.9 22.6h3.2l3.8-10.4c.5-1.5-.3-2.7-1.9-2.7h-2.7l3-8.5h.7c8.3 0 15 6.7 15 15s-6.7 15-15 15z" />
            </svg>
          </div>
          <form className={styles.form}>
            <Button
              disabled={isLoginProcessing}
              className={classNames(
                styles.submit,
                isLoginProcessing ? styles['submit--loading'] : ''
              )}
              onClick={initiateLogin}
            >
              {isLoginProcessing ? <Loader dimension={{ width: 24, height: 24 }} /> : 'Sign in'}
            </Button>
          </form>
          {guestLogin ? <GuestLogin onSubmit={processLogin} /> : <></>}
        </div>
      </div>
    </div>
  )
}

export default LayoutLogin
